import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, CircularProgress, IconButton, Modal, Portal, Snackbar } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import dayjs, { type Dayjs } from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';

import ArrowLeft from '../../../images/arrow-left.svg?react';
import ArrowRight from '../../../images/arrow-right.svg?react';
import Rss from '../../../images/real-time-rss.svg?react';
import { temperatureContext } from '../../../lib/context';
import { convertTemperature } from '../../../lib/convert';
import { useRenameDevice, useToggleArchive } from '../api/mutations';
import { getRealTimeById } from '../api/realtimeApi';
import {
    AT_LATEST_TOLERANCE_MS,
    DEFAULT_QUICK_RANGE,
    DEFAULT_QUICK_RANGE_KEY,
    QUICK_RANGES,
    dataUtils,
    timeUtils,
} from '../composables/utils';
import AlarmConfigForm from './AlarmConfigForm';
import PaginationIndicator from './PaginationIndicator';
import TemperatureGraph from './TemperatureGraph';
import TemperatureTable from './TemperatureTable';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // Cap width by viewport height too, so a short/zoomed viewport shrinks the
    // modal's width instead of leaving it wide-and-short (which starved the graph
    // of vertical space). Keeps a roughly stable aspect ratio.
    width: 'min(50rem, 94vw, 96vh)',
    bgcolor: '#FFFFFF',
    borderRadius: '1.75rem',
    boxShadow: 'none',
    p: 0,
    // Fixed size at every breakpoint so the dialog never resizes when paging
    // between sections — only the lower half slides.
    height: 'min(840px, 92vh)',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'hidden',
};

// Relayout deltas below this are treated as noise rather than a pan: Plotly
// emits the event for resizes and for our own range writes, and reacting to
// those would have the clamp fighting the chart in a loop.
const PAN_COMMIT_EPSILON_MS = 1_000;

// How far before an empty stretch to look before accepting that a device simply
// has no older readings. Wide enough to see past a realistic offline spell, so a
// gap in the data isn't mistaken for the start of it.
const HISTORY_FLOOR_PROBE_DAYS = 90;

// Horizontal slide between the three sections. `custom` carries the direction
// (+1 next / -1 prev) so the entering and exiting panes move the same way.
const slideVariants = {
    enter: (dir: any) => ({ x: dir >= 0 ? '100%' : '-100%' }),
    center: { x: 0 },
    exit: (dir: any) => ({ x: dir >= 0 ? '-100%' : '100%' }),
};

// Borderless From/To inputs that live inside a single rounded capsule. Values
// render in blue (#2B7FFF); the built-in picker button is hidden in favour of
// one leading calendar icon on the capsule.
const capsuleInputSx = {
    width: '90px',
    '& .MuiInput-root': {
        marginTop: 0,
    },
    '& .MuiInput-root:before, & .MuiInput-root:after, & .MuiInput-root:hover:not(.Mui-disabled):before': {
        borderBottom: 'none',
    },
    '& .MuiInputBase-input': {
        color: '#2B7FFF',
        fontWeight: 700,
        fontSize: '0.9rem',
        padding: 0,
        cursor: 'pointer',
    },
};

function DeviceDetailsModal({ record, open, onClose, isArchived }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const tempUnit = useContext(temperatureContext);

    const [page, setPage] = useState(0);
    const [direction, setDirection] = useState(0);
    const [detail, setDetail] = useState<any[]>([]);
    const [detailLoading, setDetailLoading] = useState(true);
    const [editName, setEditName] = useState(false);
    const renameDevice = useRenameDevice();
    const toggleArchive = useToggleArchive();
    const archiving = toggleArchive.isPending;
    const [archiveFailed, setArchiveFailed] = useState(false);

    // Per-dialog time range (defaults to the active quick range so the initial
    // window matches the highlighted pill). Local state lets each device be
    // inspected independently.
    const defaultRange = timeUtils.getQuickRange(DEFAULT_QUICK_RANGE.amount, DEFAULT_QUICK_RANGE.unit);
    const [rangeStart, setRangeStart] = useState(defaultRange.start);
    const [rangeEnd, setRangeEnd] = useState(defaultRange.end);
    const [activeRangeKey, setActiveRangeKey] = useState<string | null>(DEFAULT_QUICK_RANGE_KEY);
    const [openStart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);
    // Whether the window still ends at "now". Panning back (or picking a
    // historical end date) detaches it, which suspends live tailing — see the
    // ref mirror below.
    const [anchoredToNow, setAnchoredToNow] = useState(true);

    const nameInput = useRef<HTMLInputElement>(null);
    const isInitialLoading = useRef(false);

    // The span `detail` actually covers, which is deliberately wider than what's
    // on screen. Panning only feels like scrolling one continuous chart if there
    // is already real data beyond both edges to scroll onto; without this buffer
    // the trace would stop dead at the edge of the visible window.
    const loadedSpan = useRef<{ start: Dayjs | null; end: Dayjs | null }>({ start: null, end: null });

    // Panning can outrun the network. Every fetch takes a ticket so a slow
    // response for ground the user has already left can't clobber the screen.
    const requestSeq = useRef(0);

    // Oldest instant the chart may show, once the device is known to have no
    // history before it. Held in a ref for the loader and mirrored into state
    // for the chart, which needs to re-render to enforce it.
    const historyFloor = useRef<Dayjs | null>(null);
    const [historyFloorAt, setHistoryFloorAt] = useState<Dayjs | null>(null);
    useEffect(() => {
        historyFloor.current = historyFloorAt;
    }, [historyFloorAt]);

    // newUpdate() runs from an effect keyed on `record`, so it would otherwise
    // close over whatever `anchoredToNow` was when that effect last ran. The ref
    // gives the guard the current value.
    const anchoredToNowRef = useRef(true);
    useEffect(() => {
        anchoredToNowRef.current = anchoredToNow;
    }, [anchoredToNow]);

    // Distinguishes a range change caused by dragging the chart from one caused
    // by a preset or picker. A pan must extend the loaded series; anything else
    // is a jump to a new period and starts fresh.
    const rangeChangeIsPan = useRef(false);

    // The slice of the loaded series the chart shows, in the same format the
    // plotted points use so Plotly can line them up.
    const xRange = [timeUtils.formatDisplayTime(rangeStart), timeUtils.formatDisplayTime(rangeEnd)];

    useEffect(() => {
        if (!open) return;
        if (rangeChangeIsPan.current) {
            rangeChangeIsPan.current = false;
            ensureCoverage(rangeStart, rangeEnd);
            return;
        }
        handleUpdateDevice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, rangeStart, rangeEnd]);

    useEffect(() => {
        if (open) {
            newUpdate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record]);

    const fetchSpan = async (start: any, end: any) => {
        const data = await getRealTimeById(
            record.deviceId,
            dataUtils.formatTimeForAPI(start),
            dataUtils.formatTimeForAPI(end)
        );

        return data.detail.map((item: any) => ({
            time: timeUtils.formatDisplayTime(item.time),
            // Raw timestamp kept alongside the display string: deciding which
            // threshold set applied to a past reading needs a real instant, and
            // the formatted string has lost the offset.
            timestamp: item.time,
            temperature: convertTemperature(item.temperature, tempUnit),
            gatewayId: item.gateway_id,
        }));
    };

    // One continuous, time-ordered series. Chunks arrive from either end as the
    // user pans, and the overlap between a new chunk and what's already held is
    // deduped by timestamp so a point can't be plotted twice.
    const mergeDetail = (existing: any[], incoming: any[]) => {
        if (!incoming.length) return existing;
        const byTime = new Map();
        for (const point of existing) byTime.set(point.time, point);
        for (const point of incoming) byTime.set(point.time, point);
        return [...byTime.values()].sort((a: any, b: any) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
    };

    /**
     * Guarantee `detail` covers a buffer either side of the visible window, so
     * there is always chart to pan onto. Only the missing edges are fetched —
     * panning back over ground already loaded costs nothing and shows no spinner.
     */
    const ensureCoverage = async (visibleStart: any, visibleEnd: any, { blocking = false } = {}) => {
        const width = dayjs(visibleEnd).diff(dayjs(visibleStart));
        if (!(width > 0)) return;

        const now = dayjs();
        const floor = historyFloor.current;

        let wantStart = dayjs(visibleStart).subtract(width, 'millisecond');
        if (floor && wantStart.isBefore(floor)) wantStart = floor;
        const uncappedEnd = dayjs(visibleEnd).add(width, 'millisecond');
        const wantEnd = uncappedEnd.isAfter(now) ? now : uncappedEnd;

        const held = loadedSpan.current;
        let earlierGap = null;
        let laterGap = null;

        if (!held.start || !held.end) {
            earlierGap = [wantStart, wantEnd];
        } else {
            if (wantStart.isBefore(held.start)) earlierGap = [wantStart, held.start];
            if (wantEnd.isAfter(held.end)) laterGap = [held.end, wantEnd];
        }
        if (!earlierGap && !laterGap) return;

        const ticket = (requestSeq.current += 1);
        if (blocking) {
            isInitialLoading.current = true;
            setDetailLoading(true);
        }

        try {
            const [earlier, later] = await Promise.all([
                earlierGap ? fetchSpan(earlierGap[0], earlierGap[1]) : Promise.resolve(null),
                laterGap ? fetchSpan(laterGap[0], laterGap[1]) : Promise.resolve(null),
            ]);
            if (ticket !== requestSeq.current) return;

            setDetail((previous: any) => [earlier, later].filter(Boolean).reduce(mergeDetail, previous));
            loadedSpan.current = {
                start: held.start && held.start.isBefore(wantStart) ? held.start : wantStart,
                end: held.end && held.end.isAfter(wantEnd) ? held.end : wantEnd,
            };

            // An empty chunk from the earlier side may just be an offline gap, so
            // confirm before declaring the device has no more history.
            if (held.start && earlierGap && earlier && earlier.length === 0 && !historyFloor.current) {
                await confirmHistoryFloor(earlierGap[0], held.start);
            }
        } catch (error) {
            console.error('Error updating device data:', error);
        } finally {
            if (blocking && ticket === requestSeq.current) {
                isInitialLoading.current = false;
                setDetailLoading(false);
            }
        }
    };

    /**
     * Decide whether an empty stretch really is the start of the device's
     * history rather than a spell of it being offline.
     *
     * One empty window proves nothing — a device silent for a day would look
     * identical with a 24h view. So a much wider span before it is probed, and
     * only if that is also empty is the floor recorded. Getting this wrong
     * strands the user short of data that does exist, which is worse than
     * letting them drift a little further into empty time.
     */
    const confirmHistoryFloor = async (emptyFrom: any, knownEarliest: any) => {
        try {
            const probeFrom = dayjs(emptyFrom).subtract(HISTORY_FLOOR_PROBE_DAYS, 'day');
            const probe = await fetchSpan(probeFrom, emptyFrom);
            if (probe.length > 0) return;

            // Pin the floor to the oldest reading actually held, so the chart
            // stops with real data on screen rather than in blank space. Ref set
            // synchronously so a follow-up ensureCoverage in the same batch sees
            // it — the state update alone lags behind by an effect flush.
            historyFloor.current = knownEarliest;
            setHistoryFloorAt(knownEarliest);
        } catch {
            // A failed probe just leaves panning unrestricted, which is the safe
            // direction to err in.
        }
    };

    // A range set from outside the chart (presets, pickers, first open) replaces
    // what's held rather than extending it, so a jump to an unrelated period
    // doesn't drag the whole intervening history along with it.
    const handleUpdateDevice = async () => {
        loadedSpan.current = { start: null, end: null };
        // The floor is inferred, so a deliberate jump to another period is the
        // right moment to let it be re-derived rather than trusting an old guess.
        historyFloor.current = null;
        setHistoryFloorAt(null);
        setDetail([]);
        await ensureCoverage(rangeStart, rangeEnd, { blocking: true });
    };

    const newUpdate = async () => {
        if (isInitialLoading.current) return;
        // Live tailing appends the last few minutes to whatever is on screen. If
        // the user has panned into the past, those points belong to a different
        // window entirely and would graft today's readings onto history.
        if (!anchoredToNowRef.current) return;
        try {
            const recentTimeRange = timeUtils.getRecentTimeRange(dayjs());
            const startTimeFormatted = dataUtils.formatTimeForAPI(recentTimeRange.start);
            const endTimeFormatted = dataUtils.formatTimeForAPI(recentTimeRange.end);

            const data = await getRealTimeById(record.deviceId, startTimeFormatted, endTimeFormatted);

            const lastDetailTime = detail.length > 0 ? detail[detail.length - 1].time : null;
            const filteredDetail = data.detail.filter(
                (item: any) => !lastDetailTime || dayjs(item.time).isAfter(dayjs(lastDetailTime))
            );

            const newDetail = filteredDetail.map((item: any) => ({
                time: timeUtils.formatDisplayTime(item.time),
                // Raw timestamp kept alongside the display string: deciding which
                // threshold set applied to a past reading needs a real instant, and
                // the formatted string has lost the offset.
                timestamp: item.time,
                temperature: convertTemperature(item.temperature, tempUnit),
                gatewayId: item.gateway_id,
            }));

            setDetail((prevDetail: any) => [...prevDetail, ...newDetail]);
        } catch (error) {
            console.error('Error updating recent data:', error);
        }
    };

    // Quick range: a span ending "now", e.g. 7D => [now - 7 days, now].
    const handleQuickRange = (key: any, amount: any, unit: any) => {
        setActiveRangeKey(key);
        const { start, end } = timeUtils.getQuickRange(amount, unit);
        setRangeStart(start);
        setRangeEnd(end);
        setAnchoredToNow(true);
    };

    // Manual edits count as a custom range, so clear any active quick-range pill.
    const handleStartChange = (value: any) => {
        if (!value || !value.isValid()) return;
        setActiveRangeKey(null);
        setRangeStart(value);
    };

    const handleEndChange = (value: any) => {
        if (!value || !value.isValid()) return;
        setActiveRangeKey(null);
        setRangeEnd(value);
        setAnchoredToNow(dayjs().diff(value) <= AT_LATEST_TOLERANCE_MS);
    };

    /**
     * Commit the axis Plotly settled on after a drag.
     *
     * Reading the resulting range (rather than tracking pointer deltas) keeps
     * the committed window exactly equal to what's on screen, and means the
     * pickers can't drift out of step with the chart.
     */
    const handlePanEnd = (from: any, to: any) => {
        const now = dayjs();
        const clamped = timeUtils.clampWindowToNow(from, to, now);
        if (!clamped) return;

        // Plotly emits relayout for resizes and for our own range writes too.
        // Without this the clamp would bounce against Plotly forever.
        const unchanged =
            Math.abs(clamped.start.diff(rangeStart)) < PAN_COMMIT_EPSILON_MS &&
            Math.abs(clamped.end.diff(rangeEnd)) < PAN_COMMIT_EPSILON_MS;
        if (unchanged) return;

        rangeChangeIsPan.current = true;
        setRangeStart(clamped.start);
        setRangeEnd(clamped.end);
        setAnchoredToNow(clamped.anchoredToNow);
        // The window no longer ends at "now", so no preset describes it.
        setActiveRangeKey(null);
    };

    const closeDetail = (e: any) => {
        e.stopPropagation();
        onClose();
    };

    const onEditName = () => {
        setEditName(true);
    };

    const onSaveName = () => {
        const name = nameInput.current?.value.trim();
        if (!name) {
            setEditName(false);
            return;
        }
        // Shows on the card and here at once (the cached record is patched), and
        // reverts if the save fails.
        renameDevice.mutate({ deviceId: record.deviceId, name });
        setEditName(false);
    };

    // The backend clears the notification flags itself on archive, so this is the only request.
    const onToggleArchive = () => {
        toggleArchive.mutate(
            { deviceId: record.deviceId, archived: !isArchived },
            { onSuccess: onClose, onError: () => setArchiveFailed(true) }
        );
    };

    const unitLabel = tempUnit === 'fahrenheit' ? '°F' : '°C';

    const pageTitles = [t('trendGraph'), t('temperatureRecords'), t('configure')];

    const pageComponents = [
        <TemperatureGraph
            key='graph'
            record={record}
            detail={detail}
            xRange={xRange}
            minTime={historyFloorAt}
            onPanEnd={handlePanEnd}
        />,
        <TemperatureTable key='table' detail={detail} record={record} />,
        <AlarmConfigForm
            key='config'
            record={record}
            compact
            onClose={onClose}
            isArchived={isArchived}
            onToggleArchive={onToggleArchive}
            archiving={archiving}
        />,
    ];

    const activeContent = pageComponents[page];
    const isConfigurePage = page === pageComponents.length - 1;

    return (
        <Modal
            open={open}
            onClose={closeDetail}
            aria-labelledby='device-details-title'
            aria-describedby='device-details-description'
        >
            <Box sx={modalStyle}>
                <div className='flex h-full flex-1 flex-col gap-2 overflow-y-auto rounded-[28px] bg-white text-slate-600'>
                    <IconButton
                        className='text-slate-400 transition-colors hover:text-slate-600'
                        onClick={closeDetail}
                        size='small'
                        aria-label='Close details'
                        sx={{
                            position: 'absolute',
                            top: { xs: '1.25rem', md: '1.5rem' },
                            right: { xs: '1.25rem', md: '1.5rem' },
                            width: '2rem',
                            height: '2rem',
                        }}
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                    {/* Device summary (name, reading, refresh, identifiers) */}
                    <div className='px-5 pt-4 md:px-8 md:pt-5 '>
                        <div className='w-full shrink-0 rounded-3xl bg-white p-2 md:flex md:items-start md:justify-between md:gap-8 md:p-4'>
                            <div className='flex w-full flex-col items-center gap-3 text-center md:w-auto md:items-start md:text-left '>
                                <div className='flex items-center justify-center gap-2 self-stretch md:justify-start md:self-start'>
                                    {editName ? (
                                        <input
                                            ref={nameInput}
                                            defaultValue={record.name}
                                            className='w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 focus:border-blue-500 focus:outline-none md:w-64'
                                        />
                                    ) : (
                                        <span className='text-lg font-semibold text-slate-700 md:rounded-full md:border md:border-slate-300 md:px-4 md:py-2 md:text-base'>
                                            {record.name}
                                        </span>
                                    )}
                                    {editName ? (
                                        <Button
                                            variant='contained'
                                            size='small'
                                            className='rounded-full px-4 py-1 text-xs normal-case'
                                            onClick={onSaveName}
                                        >
                                            {t('confirm')}
                                        </Button>
                                    ) : (
                                        <IconButton
                                            size='small'
                                            onClick={onEditName}
                                            className='text-slate-400 transition-colors hover:text-slate-600'
                                            aria-label='Edit device name'
                                        >
                                            <EditIcon fontSize='small' />
                                        </IconButton>
                                    )}
                                </div>
                            </div>

                            <div className='mt-0 flex w-full flex-col items-center gap-3 text-slate-600 md:mt-0 md:w-auto md:items-end md:text-right'>
                                <div className='flex items-end gap-2 text-slate-800 md:order-2'>
                                    <span className='text-5xl font-semibold leading-none md:text-6xl'>
                                        {record.lastItem.temperature ?? '--'}
                                    </span>
                                    <span className='pb-1 text-lg font-semibold md:text-xl'>{unitLabel}</span>
                                </div>
                                <div className='flex items-end gap-2 text-sm md:order-1 md:justify-end'>
                                    {record.connected ? (
                                        <Rss className='h-4 w-4 text-slate-400' />
                                    ) : (
                                        <img src='/offline.svg' alt='Offline' className='h-4 w-4' />
                                    )}
                                    <span className='text-[12px] font-semibold'>
                                        {t('lastRefresh')} :{' '}
                                        <span className=' text-slate-700'>{record.lastItem.time ?? '--'}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className='mt-2 md:mt-3 flex w-full justify-between gap-8 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400 md:text-xs'>
                            <div className='flex w-full flex-col items-start justify-between gap-2 md:items-start md:gap-6'>
                                <div>
                                    <span className='whitespace-nowrap'>{t('gatewayID')} : </span>
                                    <span className='text-center text-slate-700 normal-case md:max-w-[220px] md:text-right'>
                                        {record.lastItem.gatewayId ?? '--'}
                                    </span>
                                </div>
                                <div>
                                    <span className='whitespace-nowrap'>{t('deviceID')} : </span>
                                    <span className='text-slate-700 normal-case md:text-right'>{record.deviceId}</span>
                                </div>
                            </div>
                            <div className='flex w-full max-w-[200px] flex-col items-start justify-between gap-2 md:items-start md:gap-6'>
                                <div>
                                    <span className='whitespace-nowrap'>{t('upperLimit')} : </span>
                                    <span className='text-slate-700 normal-case'>
                                        {record.activeUpperLimit}
                                        {unitLabel}
                                    </span>
                                </div>
                                <div>
                                    <span className='whitespace-nowrap'>{t('lowerLimit')} : </span>
                                    <span className='text-slate-700 normal-case'>
                                        {record.activeLowerLimit}
                                        {unitLabel}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='mb-2 mt-1 md:mt-0 md:mb-0 h-px bg-slate-200 md:mx-8' />

                    {/* Section title (fixed; updates with the active page) */}
                    <div className='shrink-0 px-5 pt-1 text-center text-xl font-semibold text-slate-700 md:px-8 md:pt-2'>
                        {pageTitles[page]}
                    </div>

                    {/* Time-range controls (shared by graph/table; hidden on configure) */}
                    {!isConfigurePage && (
                        <div className='shrink-0 px-5 pt-2 md:px-8 md:pt-3'>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className='mx-auto flex w-fit max-w-full items-center gap-1.5 overflow-x-auto rounded-full border border-slate-200 bg-white px-4 py-1.5 shadow-sm'>
                                    <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: '#94A3B8', flexShrink: 0 }} />
                                    <span className='shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400'>
                                        {t('from')}
                                    </span>
                                    <DateTimePicker
                                        open={openStart}
                                        onOpen={() => setOpenStart(true)}
                                        onClose={() => setOpenStart(false)}
                                        value={rangeStart}
                                        onChange={handleStartChange}
                                        format='MM/DD HH:mm'
                                        maxDateTime={rangeEnd}
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                onClick: () => setOpenStart(true),
                                                InputProps: { disableUnderline: true },
                                                sx: capsuleInputSx,
                                            },
                                            inputAdornment: { sx: { display: 'none' } },
                                        }}
                                    />
                                    <span className='shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400'>
                                        {t('to')}
                                    </span>
                                    <DateTimePicker
                                        open={openEnd}
                                        onOpen={() => setOpenEnd(true)}
                                        onClose={() => setOpenEnd(false)}
                                        value={rangeEnd}
                                        onChange={handleEndChange}
                                        format='MM/DD HH:mm'
                                        minDateTime={rangeStart}
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                onClick: () => setOpenEnd(true),
                                                InputProps: { disableUnderline: true },
                                                sx: capsuleInputSx,
                                            },
                                            inputAdornment: { sx: { display: 'none' } },
                                        }}
                                    />
                                </div>
                            </LocalizationProvider>
                            <div className='mt-2 flex flex-wrap items-center justify-center gap-2'>
                                <span className='mr-1 text-xs font-semibold uppercase tracking-wide text-slate-400'>
                                    {t('quickRange')}
                                </span>
                                {QUICK_RANGES.map(({ key, amount, unit }: any) => (
                                    <button
                                        key={key}
                                        type='button'
                                        onClick={() => handleQuickRange(key, amount, unit)}
                                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                            activeRangeKey === key
                                                ? 'bg-blue-500 text-white shadow'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {t(`quickRangeLabels.${key}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Content area — only the active page component slides. Fills the
                        available space but keeps a minimum height so a short/zoomed modal
                        can't squeeze the graph to nothing; the modal body scrolls once the
                        header + this minimum exceed the available height. */}
                    <div className='relative flex-1 min-h-[240px] overflow-hidden px-4 mt-1 md:mt-2 md:px-8'>
                        <AnimatePresence custom={direction} initial={false}>
                            <motion.div
                                key={page}
                                custom={direction}
                                variants={slideVariants}
                                initial='enter'
                                animate='center'
                                exit='exit'
                                transition={{ type: 'spring', stiffness: 380, damping: 36 }}
                                className='absolute inset-0 flex w-full flex-col p-0 md:p-4'
                            >
                                <div className='flex-1 overflow-hidden min-h-0'>
                                    <div className='relative flex h-full min-h-0 overflow-y-auto p-0 md:px-3 md:pb-3'>
                                        <div className='w-full max-w-full flex-1'>{activeContent}</div>
                                        {detailLoading && (
                                            // pointer-events-none so a slow window can't eat the
                                            // next swipe. Blocking here made panning feel stuck
                                            // exactly when the user wanted to keep moving.
                                            <div className='pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-white/70'>
                                                <CircularProgress color='primary' size={32} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* Toolbar / pagination controls */}
                <div className='flex items-center justify-between gap-4 border-t border-slate-200 bg-white px-5 py-3 md:px-8 md:py-4'>
                    <button
                        type='button'
                        onClick={() => {
                            setDirection(-1);
                            setPage((page + pageComponents.length - 1) % pageComponents.length);
                        }}
                        className='flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 cursor-pointer'
                        disabled={detailLoading}
                        aria-label='Previous section'
                    >
                        <ArrowLeft className='h-4 w-4' />
                    </button>
                    <PaginationIndicator count={pageTitles.length} activeIndex={page} />
                    <button
                        type='button'
                        onClick={() => {
                            setDirection(1);
                            setPage((page + 1) % pageComponents.length);
                        }}
                        className='flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 cursor-pointer'
                        disabled={detailLoading}
                        aria-label='Next section'
                    >
                        <ArrowRight className='h-4 w-4' />
                    </button>
                </div>
                <Portal>
                    <Snackbar open={archiveFailed} autoHideDuration={6000} onClose={() => setArchiveFailed(false)}>
                        <Alert onClose={() => setArchiveFailed(false)} severity='error'>
                            {t('archiveFail')}
                        </Alert>
                    </Snackbar>
                </Portal>
            </Box>
        </Modal>
    );
}

export default DeviceDetailsModal;
